/**
 * MarketingLayout — shell for every public marketing page.
 *
 * Provides the sticky Navbar at the top, the configurable footer at the
 * bottom, and a scroll-to-top behaviour on route change so deep links
 * never land mid-page.
 *
 * Also wires the browser tab title and <meta name="description"> from
 * config.meta — so editing site.config.json updates the tab name and
 * search-engine snippet without touching index.html.
 */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import useSiteConfig from './useSiteConfig';
import { MotionStyles, useScrollReveal, useCountUp } from './components/Motion';
import { COLOR, FONT } from '../../components/ss/tokens';

/** Set or update <meta name="description"> in the document head. */
function setMetaDescription(value) {
  if (!value) return;
  let el = document.querySelector('meta[name="description"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'description');
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

/** Set or replace <link rel="icon"> if a favicon URL is configured. */
function setFavicon(url) {
  if (!url) return;
  let el = document.querySelector('link[rel="icon"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'icon');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

export default function MarketingLayout({ children }) {
  const location = useLocation();
  const { config } = useSiteConfig();

  // Scroll behaviour on route change
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      const id = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [location.pathname, location.hash]);

  // Motion: reveal-on-scroll + metric count-up; re-scan when content/route changes
  useScrollReveal([config, location.pathname]);
  useCountUp([config, location.pathname]);

  // Page title + description + favicon — driven by config.meta
  useEffect(() => {
    const meta = config.meta || {};
    const titles = meta.titles || {};
    const title = titles[location.pathname] || meta.defaultTitle;
    if (title) document.title = title;
    setMetaDescription(meta.defaultDescription);
    setFavicon(meta.faviconUrl);
  }, [config.meta, location.pathname]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: FONT.family,
        color: COLOR.text,
        background: '#fff',
      }}
    >
      <MotionStyles />
      <Navbar />
      <main style={{ flex: 1 }}>{children}</main>
      <Footer />
    </div>
  );
}
